import { Action, PayloadAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import { call, fork, put, select, take, takeEvery } from 'typed-redux-saga';
import { v4 as uuidv4 } from 'uuid';

import { conversationBulkActionTypeMap } from 'apis/conversations';
import { notificationActions } from 'components/notifications/actions/notification';
import { WebSocketBulkActionInboundMessage } from 'middleware/webSocket';

import { conversationsBulkModalActions } from './conversationsBulkModal.actions';
import { BulkModalAction } from './types/conversationsBulkModal.types';
import {
  ConversationBulkModalEndpointCallPayload,
  ConversationsBulkModalInitPayload,
} from './types/conversationsBulkModalActions.types';
import { conversationsBulkModalActionEndpoint } from './utils/conversationsBulkModalActionEndpoint';
import { conversationsBulkModalActionLabel } from './utils/conversationsBulkModalActionLabel';

export const conversationsBulkModalSagas = [
  takeEvery(conversationsBulkModalActions.initBulkUpdate, handleInit),
  takeEvery(conversationsBulkModalActions.callEndpoint, handleEndpointCall),
];

/**
 * Handles the bulk action WebSocket responses.
 *
 * @param action The bulk modal action.
 */
function* handleWebSocketResponse(action: BulkModalAction) {
  const { type: actionType } = action;
  const bulkActionStartedAction = yield* take<
    PayloadAction<WebSocketBulkActionInboundMessage>
  >(
    (action) =>
      action.type === conversationsBulkModalActions.startBulkUpdate.type &&
      conversationBulkActionTypeMap[action.payload.params.action_type] ===
        actionType &&
      action.payload.type === 'bulkaction:started',
  );

  const idPrefix = 'bulk_';

  yield put(
    notificationActions.notificationAdd({
      id: `${idPrefix}${bulkActionStartedAction.payload.params.bulk_id}`,
      title: bulkActionStartedAction.payload.params.subject,
      text: bulkActionStartedAction.payload.params.message,
      icon: 'sync',
    }),
  );

  const bulkActionFinishedAction = yield* take<
    PayloadAction<WebSocketBulkActionInboundMessage>
  >(
    (action) =>
      action.type === conversationsBulkModalActions.finishBulkUpdate.type &&
      conversationBulkActionTypeMap[action.payload.params.action_type] ===
        actionType &&
      action.payload.type === 'bulkaction:finished',
  );

  yield put(
    notificationActions.notificationRemoveById({
      id: `${idPrefix}${bulkActionFinishedAction.payload.params.bulk_id}`,
    }),
  );

  const isBulkActionFulfilled =
    bulkActionFinishedAction.payload.params.notification_type ===
    'BulkActionSucceeded';
  const isBulkActionRejected =
    bulkActionFinishedAction.payload.params.notification_type ===
    'BulkActionFailed';

  yield put(
    notificationActions.notificationAdd({
      icon: isBulkActionFulfilled ? 'done' : 'exclamation',
      title: bulkActionFinishedAction.payload.params.subject,
      text: bulkActionFinishedAction.payload.params.message,
      time: 5000,
      status: isBulkActionFulfilled
        ? 'success'
        : isBulkActionRejected
        ? 'danger'
        : 'warning',
    }),
  );
}

/**
 * Handles the erroneous bulk action rest api response.
 *
 * @param action The bulk modal action.
 * @param errorMessage The error message.
 */
function* handleEndpointError(action: BulkModalAction) {
  const { type } = action;
  yield put(
    notificationActions.notificationAdd({
      title: `Could not start bulk action: ${conversationsBulkModalActionLabel[type]}.`,
      icon: 'exclamation',
      status: 'danger',
    }),
  );
}

/**
 * Handles making the endpoint call for a bulk modal action.
 *
 * @param action The bulk modal action.
 */
function* handleEndpointCall({
  payload: { action },
}: PayloadAction<ConversationBulkModalEndpointCallPayload>) {
  // We fork the WebSocket response handler as WebSocket might respond before
  // rest api response comes back.
  const webSocketResponseTask = yield* fork(handleWebSocketResponse, action);
  const { type, arg } = action;
  const endpoint = conversationsBulkModalActionEndpoint[type];
  const promise = yield* put(yield* call(endpoint.initiate, arg));

  yield promise;

  const response = yield* select(endpoint.select(promise.requestId));

  if (response.error) {
    webSocketResponseTask.cancel();
    yield call(handleEndpointError, action);
  }
}

/**
 * Handles the initialization of a bulk modal action.
 *
 * @param action The bulk modal action.
 */
function* handleInitAction(action: BulkModalAction) {
  const id = uuidv4();
  const {
    type: actionType,
    arg: { csids },
  } = action;

  yield put(
    notificationActions.notificationAdd({
      id,
      title: `Bulk action: ${conversationsBulkModalActionLabel[actionType]}`,
      text: `${csids.length} ${
        csids.length === 1 ? 'conversation' : 'conversations'
      }`,
      icon: 'info',
      time: 10000,
      rightAction: {
        text: 'Cancel',
        // TODO: [TEAMS-8993] Use dispatch from the callback instead
        action: function* () {
          yield* put(
            notificationActions.notificationRemoveAllById({
              id,
            }),
          );
        },
      },
      actionOnTimeout: (dispatch: Dispatch<Action>) => {
        dispatch(
          notificationActions.notificationRemoveAllById({
            id,
          }),
        );

        dispatch(conversationsBulkModalActions.callEndpoint({ action }));
      },
      closeAction: function* () {
        yield* put(
          notificationActions.notificationRemoveAllById({
            id,
          }),
        );

        yield* put(conversationsBulkModalActions.callEndpoint({ action }));
      },
    }),
  );
}

/** Handles the initialization of the conversations bulk modal update flow. */
function* handleInit({
  payload: { actions },
}: PayloadAction<ConversationsBulkModalInitPayload>) {
  for (const action of actions) {
    yield fork(handleInitAction, action);
  }
}
